/**
 * Shared helpers for personal chat inbox ordering (most recent activity first).
 */

import { imageMessagePreviewText } from "../../lib/contentImages";

/**
 * Timestamp used for recency sorting / display.
 * @param {object} chat
 * @returns {string}
 */
export function getChatActivityTime(chat) {
  if (!chat) return "";
  return chat.last_message_timestamp || chat.last_message_at || "";
}

/**
 * Sort chats from most recent activity to oldest.
 * @param {Array<object>} chats
 * @returns {Array<object>}
 */
export function sortChatsByRecent(chats) {
  return [...(chats || [])].sort((a, b) => {
    const tb = getChatActivityTime(b);
    const ta = getChatActivityTime(a);
    if (ta !== tb) {
      if (!ta) return 1;
      if (!tb) return -1;
      return tb.localeCompare(ta);
    }
    return Number(b.id) - Number(a.id);
  });
}

/**
 * Insert or replace a chat and place it at the front of the list.
 * @param {Array<object>} list
 * @param {object} chat
 * @returns {Array<object>}
 */
export function upsertChat(list, chat) {
  if (!chat?.id) return list || [];
  const id = Number(chat.id);
  const rest = (list || []).filter((c) => Number(c.id) !== id);
  return [chat, ...rest];
}

/**
 * Merge a chat into the inbox and keep most-recent-message order.
 * Selecting or updating a chat does not bump it to the top.
 *
 * @param {Array<object>} list
 * @param {object} chat
 * @returns {Array<object>}
 */
export function mergeChatByRecent(list, chat) {
  if (!chat?.id) return list || [];
  const id = Number(chat.id);
  const existing = (list || []).find((c) => Number(c.id) === id);
  const merged = existing ? { ...existing, ...chat } : chat;
  if (existing && !getChatActivityTime(chat) && getChatActivityTime(existing)) {
    merged.last_message_timestamp = existing.last_message_timestamp;
    merged.last_message_at = existing.last_message_at;
    if (!merged.last_message_content) {
      merged.last_message_content = existing.last_message_content;
    }
  }
  const rest = (list || []).filter((c) => Number(c.id) !== id);
  return sortChatsByRecent([merged, ...rest]);
}

/**
 * Update a chat's last message fields and move it to the top of the list.
 * @param {Array<object>} list
 * @param {number|string} chatId
 * @param {string} messageContent
 * @param {string} [messageTimestamp]
 * @param {{ incrementUnread?: boolean }} [options]
 * @returns {Array<object>}
 */
export function bumpChatForNewMessage(
  list,
  chatId,
  messageContent,
  messageTimestamp,
  options = {}
) {
  const id = Number(chatId);
  if (!Number.isFinite(id)) return list || [];
  const incrementUnread = Boolean(options.incrementUnread);

  let updated = null;
  const rest = [];
  for (const chat of list || []) {
    if (Number(chat.id) === id) {
      const ts =
        messageTimestamp ||
        chat.last_message_timestamp ||
        chat.last_message_at ||
        "";
      updated = {
        ...chat,
        last_message_content: messageContent,
        last_message_timestamp: ts,
        last_message_at: ts || chat.last_message_at,
        unread_count: incrementUnread
          ? (Number(chat.unread_count) || 0) + 1
          : chat.unread_count,
      };
    } else {
      rest.push(chat);
    }
  }
  if (!updated) return list || [];
  return [updated, ...rest];
}

/**
 * Clear unread for a chat in the list (immutable).
 * @param {Array<object>} list
 * @param {number|string} chatId
 * @returns {Array<object>}
 */
export function clearChatUnread(list, chatId) {
  const id = Number(chatId);
  if (!Number.isFinite(id)) return list || [];
  return (list || []).map((chat) =>
    Number(chat.id) === id ? { ...chat, unread_count: 0 } : chat
  );
}

/**
 * Sum unread counts across chats.
 * @param {Array<object>} chats
 * @returns {number}
 */
export function getTotalUnreadCount(chats) {
  return (chats || []).reduce(
    (sum, chat) => sum + (Number(chat.unread_count) || 0),
    0
  );
}

/**
 * Inbox / thread preview for a message, including shared post/event fallbacks.
 * @param {object|null|undefined} message
 * @returns {string}
 */
export function getSharedMessagePreview(message) {
  if (!message) return "";
  const content = (message.content || "").trim();
  if (content) return message.content;
  const type = message.message_type;
  if (type === "image") return imageMessagePreviewText(message);
  if (type !== "post" && type !== "event") return message.content || "";
  let hasComment = false;
  try {
    const raw = message.metadata;
    const meta = typeof raw === "string" ? JSON.parse(raw || "{}") : raw;
    hasComment = Boolean(meta && meta.comment);
  } catch {
    hasComment = false;
  }
  if (hasComment) return "Shared a comment";
  if (type === "event") return "Shared an event";
  return "Shared a post";
}

/**
 * Whether an inbox event should bump unread for this viewer.
 * System messages and own messages never count as unread.
 * @param {object} message
 * @param {number|string} viewerUserId
 * @param {number|string|null|undefined} openChatId
 * @returns {boolean}
 */
export function shouldIncrementUnread(message, viewerUserId, openChatId) {
  if (!message) return false;
  if (message.message_type === "system") return false;
  if (message.deleted_at) return false;
  if (Number(message.sender_id) === Number(viewerUserId)) return false;
  if (
    openChatId != null &&
    Number(message.chat_id) === Number(openChatId)
  ) {
    return false;
  }
  return true;
}

/**
 * Org hub ordering: default/general channels first, then by recent activity.
 * @param {Array<object>} chats
 * @returns {Array<object>}
 */
export function sortOrgChannelsByActivity(chats) {
  const list = [...(chats || [])];
  const defaults = list.filter((c) => c.is_default);
  const rest = list.filter((c) => !c.is_default);
  defaults.sort((a, b) => {
    const byName = (a.name || "").localeCompare(b.name || "");
    if (byName !== 0) return byName;
    return Number(a.id) - Number(b.id);
  });
  return [...defaults, ...sortChatsByRecent(rest)];
}

/**
 * Update an org channel's last message / unread and re-sort with general pinned.
 * @param {Array<object>} list
 * @param {number|string} chatId
 * @param {string} messageContent
 * @param {string} [messageTimestamp]
 * @param {{ incrementUnread?: boolean }} [options]
 * @returns {Array<object>}
 */
export function bumpOrgChannelForNewMessage(
  list,
  chatId,
  messageContent,
  messageTimestamp,
  options = {}
) {
  const id = Number(chatId);
  if (!Number.isFinite(id)) return list || [];
  if (!(list || []).some((c) => Number(c.id) === id)) return list || [];
  return sortOrgChannelsByActivity(
    bumpChatForNewMessage(
      list,
      chatId,
      messageContent,
      messageTimestamp,
      options
    )
  );
}
